'use client'

import { useEffect, useState } from 'react'
import { HttpTypes } from '@medusajs/types'
import { getCategoryIdByHandle } from '../medusaData/categories'
import { listProducts } from '../medusaData/products'
import { getRegion } from '../medusaData/regions'
import { productHasActivePromotion } from '../utils/product-has-promotion'

interface UseFetchProductsParams {
  countryCode?: string
  categoryHandle?: string | null
  limit?: number
  /** Fetch every page from Medusa instead of just the first one. */
  fetchAllPages?: boolean
  /** Ignore categoryHandle and keep only products with an active promotion. */
  onlyPromotions?: boolean
}

interface UseFetchProductsResult {
  products: HttpTypes.StoreProduct[]
  loading: boolean
  error: unknown
}

export function useFetchProducts({
  countryCode = 'be',
  categoryHandle,
  limit,
  fetchAllPages = false,
  onlyPromotions = false,
}: UseFetchProductsParams = {}): UseFetchProductsResult {
  const [products, setProducts] = useState<HttpTypes.StoreProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let cancelled = false

    const fetchProducts = async () => {
      setLoading(true)
      setError(null)

      try {
        const region = await getRegion(countryCode)

        if (!region) {
          if (!cancelled) {
            setProducts([])
          }
          return
        }

        let categoryId: string | null | undefined
        if (categoryHandle && !onlyPromotions) {
          categoryId = await getCategoryIdByHandle(categoryHandle)
        }

        let allProducts: HttpTypes.StoreProduct[] = []
        let pageParam = 1
        let nextPage: number | null = 1

        while (nextPage) {
          const { response, nextPage: next } = await listProducts({
            regionId: region.id,
            pageParam,
            queryParams: {
              limit,
              category_id: categoryId ? [categoryId] : undefined,
            },
          })

          allProducts = allProducts.concat(response.products)
          nextPage = fetchAllPages || onlyPromotions ? next : null
          pageParam += 1
        }

        if (onlyPromotions) {
          allProducts = allProducts.filter(productHasActivePromotion)
        }

        if (!cancelled) {
          setProducts(allProducts)
        }
      } catch (err) {
        console.error('Erreur fetch produits: ', err)
        if (!cancelled) {
          setError(err)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchProducts()

    return () => {
      cancelled = true
    }
  }, [countryCode, categoryHandle, limit, fetchAllPages, onlyPromotions])

  return { products, loading, error }
}
